<template>
  <section class="pstats">
    <!-- Stat picker; in the table view it is also the column header. -->
    <div class="pstats__bar" :class="{ 'is-table': tableView }" :style="tableView ? gridStyle : null">
<!--      <button type="button" class="pstats__btn pstats__btn&#45;&#45;table" :class="{ 'is-on': tableView }"-->
<!--              :title="$t('statistics.playertable')" @click="tableView = !tableView">-->
<!--        <v-icon size="18">mdi-table-large</v-icon>-->
<!--      </button>-->
      <button v-for="col in columns" :key="col.key" type="button" class="pstats__btn"
              :class="{ 'is-active': col.key === selected.key }" :title="$t(col.label)" @click="selectedKey = col.key">
        <span v-if="col.svg" class="pstats__svg" v-html="col.svg"></span>
        <span v-else-if="col.text" class="pstats__text">{{ col.text }}</span>
        <v-icon v-else size="16">{{ col.icon }}</v-icon>
      </button>
    </div>

    <!-- Every player who played, sorted by the picked stat. -->
    <div v-if="tableView" class="pstats__table">
      <div v-for="p in tableRows" :key="p.key" class="pstats__trow" :style="gridStyle">
        <div class="pstats__who">
          <LogoCompetitors v-if="p.teamId" :team-id="p.teamId" height="14px" width="14px" class="pstats__logo"/>
          <span class="pstats__name text-truncate" :title="p.name">{{ p.short }}</span>
          <template v-for="m in p.marks">
            <img v-if="m.img" :key="m.key" class="pstats__mark" :src="m.img" :title="m.title" alt="">
            <span v-else :key="m.key" class="pstats__mark" :title="m.title" v-html="m.svg"></span>
          </template>
        </div>
        <div v-for="col in columns" :key="col.key" class="pstats__cell" :class="{ 'is-zero': !p.stats[col.key] }"
             :title="p.stats[col.key] === undefined ? $t('statistics.playerstatmissing') : null">
          {{ format(col, p.stats[col.key]) }}
        </div>
      </div>
    </div>

    <!-- Top players of each team for the picked stat, side by side. -->
    <div v-else class="pstats__board">
      <div class="pstats__title">{{ $t(selected.label) }}</div>
      <div class="pstats__grid">
        <template v-for="(row, idx) in boardRows">
          <div :key="'h' + idx" class="pstats__side pstats__side--home" :class="{ 'is-zero': row.home && !row.home.value }" :title="row.home && row.home.missing ? $t('statistics.playerstatmissing') : null">
            <template v-if="row.home">
              <span class="pstats__name text-truncate" :title="row.home.name">{{ row.home.short }}</span>
              <span class="pstats__value" :class="{ 'has-value': row.home.value, 'is-leader': row.home.value > ((row.away && row.away.value) || 0) }">{{ format(selected, row.home.value) }}</span>
            </template>
          </div>
          <div :key="'a' + idx" class="pstats__side pstats__side--away" :class="{ 'is-zero': row.away && !row.away.value }" :title="row.away && row.away.missing ? $t('statistics.playerstatmissing') : null">
            <template v-if="row.away">
              <span class="pstats__value" :class="{ 'has-value': row.away.value, 'is-leader': row.away.value > ((row.home && row.home.value) || 0) }">{{ format(selected, row.away.value) }}</span>
              <span class="pstats__name text-truncate" :title="row.away.name">{{ row.away.short }}</span>
            </template>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import LogoCompetitors from "@/components/center_widgets/LogoCompetitors.vue";
import {CARD_SVGS, GOAL_ICON} from "@/services/matchtracker/matchIncidents";
import {PLAYER_STAT_COLUMNS, readPlayers, shortPlayerName} from "@/services/matchtracker/playerStatsData";

// Ball outside the goal: every shot.
const SHOTS_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18V7h12v11M12 10.5h6M12 14h6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="4.2" cy="15.5" r="2.4" fill="currentColor"/></svg>';
// Ball inside the goal: shots on target.
const ON_TARGET_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 18V6h18v12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="12" cy="13.5" r="3" fill="currentColor"/></svg>';
// Pass line ending on the ball.
const ASSIST_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="4.5" cy="18" r="2.5" fill="currentColor"/><path d="M7 16l8-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="18.5" cy="7.5" r="3.5" fill="currentColor"/></svg>';

// Columns, in order (see PLAYER_STAT_COLUMNS), with their icon; a column
// shows only when the data has its stat.
const COLUMN_ICONS = {
  shots: { svg: SHOTS_SVG },
  ontarget: { svg: ON_TARGET_SVG },
  passes: { icon: 'mdi-transit-connection-variant' },
  tackles: { icon: 'mdi-shoe-cleat' },
  fouls: { icon: 'mdi-whistle-outline' },
  interceptions: { icon: 'mdi-account-multiple-outline' },
  saves: { icon: 'mdi-hand-back-left-outline' },
  xg: { text: 'xG' }
};
const COLUMNS = PLAYER_STAT_COLUMNS.map(c => ({ ...c, ...COLUMN_ICONS[c.key] }));
const BOARD_SIZE = 10;

export default {
  name: "PlayerStatsSection",
  components: { LogoCompetitors },
  props: {
    matchData: { type: Object, required: true },
    // live-scores/player-stats answer; the feed's playerStatistics without it.
    playerStats: { type: Object, default: null }
  },
  data() {
    return {
      tableView: false,
      selectedKey: null
    };
  },
  methods: {
    // "–" when the stat is not available for the player (missing from the
    // data, or a 0 the API filled in, see missingStats).
    format(col, value) {
      if (value === undefined || value === null) return '–';
      if (col.decimals) return value ? value.toFixed(col.decimals) : '–';
      return value || 0;
    }
  },
  computed: {
    // Both sources as { key, name, teamId, side, played, stats: {column: n}, extras }.
    sourcePlayers() {
      return readPlayers(this.playerStats, this.matchData);
    },
    // Players who played, with goal/assist/card marks next to the name.
    players() {
      return this.sourcePlayers.filter(p => p.played && p.name.trim()).map(p => {
        const marks = [];
        const add = (key, count, svg, title, img) => { for (let i = 0; i < count; i++) marks.push({ key: key + i, svg, title, img }); };
        add('g', p.extras.goals, null, this.$t('statistics.playergoals'), GOAL_ICON);
        add('a', p.extras.assists, ASSIST_SVG, this.$t('statistics.playerassists'));
        if (p.extras.red || p.extras.yellow >= 2) add('r', 1, CARD_SVGS.red, '');
        else if (p.extras.yellow) add('y', 1, CARD_SVGS.yellow, '');
        return { ...p, short: shortPlayerName(p.name), marks, minutes: p.extras.minutes };
      });
    },
    columns() {
      const present = new Set();
      this.sourcePlayers.forEach(p => Object.keys(p.stats).forEach(k => present.add(k)));
      return COLUMNS.filter(c => present.has(c.key));
    },
    // Picked stat, or the first column (shots) until one is picked.
    selected() {
      return this.columns.find(c => c.key === this.selectedKey) || this.columns[0] || COLUMNS[0];
    },
    gridStyle() {
      return { gridTemplateColumns: `minmax(0, 1fr) repeat(${this.columns.length}, 34px)` };
    },
    // Highest first; a stat not available ranks as 0, after the real 0s.
    sorted() {
      const key = this.selected.key;
      const val = p => p.stats[key];
      return [...this.players].sort((a, b) => (val(b) || 0) - (val(a) || 0) ||
          (val(a) === undefined) - (val(b) === undefined) || b.minutes - a.minutes);
    },
    tableRows() {
      return this.sorted;
    },
    boardRows() {
      const key = this.selected.key;
      const top = side => this.sorted.filter(p => p.side === side).slice(0, BOARD_SIZE).map(p => ({ ...p, value: p.stats[key], missing: p.stats[key] === undefined }));
      const home = top('home');
      const away = top('away');
      return Array.from({ length: Math.max(home.length, away.length) }, (_, i) => ({ home: home[i] || null, away: away[i] || null }));
    }
  }
}
</script>

<style scoped>
.pstats {
  color: var(--text-platform);
  padding: 4px 4px 12px;
}

/* Stat picker */
.pstats__bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}

.pstats__bar.is-table {
  display: grid;
  gap: 0;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 0;
}

.pstats__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 30px;
  border-radius: 8px;
  color: var(--text-op-50-platform);
  transition: background 0.15s ease, color 0.15s ease;
}

.pstats__btn .v-icon { color: inherit !important; }

.pstats__btn:hover { color: var(--text-platform); }

.pstats__btn.is-active {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-primary);
}

.pstats__btn--table.is-on { color: var(--color-primary); }

.is-table .pstats__btn--table { justify-self: start; }

.pstats__svg,
.pstats__svg ::v-deep svg {
  display: block;
  width: 18px;
  height: 18px;
}

.pstats__text {
  font-size: var(--font-size-2xl);
  font-weight: 700;
}

/* Table */
.pstats__trow {
  display: grid;
  align-items: center;
  min-height: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: var(--font-size-2xl);
}

.pstats__who {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding-left: 4px;
}

.pstats__logo {
  flex: none;
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.pstats__name { min-width: 0; }

.pstats__mark,
.pstats__mark ::v-deep svg {
  flex: none;
  display: block;
  width: 11px;
  height: 11px;
  color: var(--color-primary);
}

.pstats__cell {
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.pstats__cell.is-zero { color: var(--text-op-50-platform); }

/* Leaderboard */
.pstats__title {
  text-align: center;
  text-transform: uppercase;
  font-size: var(--font-size-md);
  font-weight: 700;
  margin-bottom: 8px;
}

.pstats__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  row-gap: 4px;
}

.pstats__side {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 24px;
  font-size: var(--font-size-2xl);
}

.pstats__side--home { justify-content: flex-end; text-align: right; }
.pstats__side--away { justify-content: flex-start; }

.pstats__side.is-zero { color: var(--text-op-50-platform); }

.pstats__value {
  position: relative;
  flex: none;
  min-width: 26px;
  padding: 2px 4px;
  border-radius: 3px;
  text-align: center;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  z-index: 0;
}

/* Tinted with the skin colour: brighter for the higher value of the row. */
.pstats__value.has-value::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: inherit;
  background: var(--color-primary);
  opacity: 0.3;
  z-index: -1;
}

.pstats__value.is-leader::before { opacity: 0.65; }
</style>
